import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectApp,
    selectRelease,
    selectTimelineEvents,
    selectAppPlatform,
    selectAppStoreType,
    selectIsDemoInstance,
    selectFilteredTimelineEvents,
    selectIsWorkflowsCIStepEnabled,
} from '../../selectors/selectors';
import { Release, ReleaseStepType, TimelineEvent } from '../../types';
import { ReleaseTimelineSectionContext } from '../timelineEvents/EventsTimeline';
import { TimelineEventsEntityRequest, tryFetchEntity, WorkflowRunEntityRequest } from '../../actions/entities';
import { FetchableEntityType, FetchableEntityTypeV2 } from '../../entityType';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useGetEntityV2, useIsLoading } from '@/utils/hooks/helpers';
import { EventsTimeline } from '../timelineEvents/EventsTimeline';
import { EventsTimelineDrawer } from '../timelineEvents/EventsTimelineDrawer';
import { displayTextForReleaseStepType } from '../../helpers/displayHelpers';
import { updateTimelineEventsFilters } from '../../actions/filters';

export interface EventsTimelineSectionProps {
    events?: TimelineEvent[];
    shouldShowFullEventTimeline?: { value: boolean };
    maxItemsToDisplay?: number;
    orgId: string;
    appId: string;
    releaseId: string;
    releaseStepType?: ReleaseStepType;
    context: ReleaseTimelineSectionContext;
    isLoading?: boolean;
    stepRunId?: string;
}

export const EventsTimelineSection: React.FC<EventsTimelineSectionProps> = ({
    events,
    shouldShowFullEventTimeline,
    maxItemsToDisplay,
    orgId,
    appId,
    releaseId,
    releaseStepType,
    isLoading,
    context,
}) => {
    const dispatch = useDispatch();

    const [isDrawerVisible, setIsDrawerVisible] = React.useState(false);

    React.useEffect(() => {
        if (shouldShowFullEventTimeline?.value) {
            setIsDrawerVisible(shouldShowFullEventTimeline?.value);
        }
    }, [shouldShowFullEventTimeline]);

    const release = useSelector((state: RootState) => selectRelease(state, releaseId)) as Release;

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    return (
        <>
            {releaseStepType != ReleaseStepType.Rollout && (
                <EventsTimeline
                    isLoading={isLoading}
                    events={events}
                    maxItemsToDisplay={maxItemsToDisplay ?? 3}
                    orgId={orgId}
                    appId={appId}
                    release={release}
                    releaseType={release.type}
                    shouldHideUpcomingEvents={context === ReleaseTimelineSectionContext.AppHome}
                    releaseStepType={releaseStepType}
                    context={context}
                />
            )}
            <EventsTimelineDrawer
                events={events}
                open={isDrawerVisible}
                onClose={() => {
                    setIsDrawerVisible(false);
                    dispatch(updateTimelineEventsFilters({ filters: [] }));
                }}
                orgId={orgId}
                appId={appId}
                releaseId={release.releaseId}
                releaseStepDisplayName={displayTextForReleaseStepType(
                    releaseStepType,
                    platform,
                    appStoreType,
                    isWorkflowsCIStepEnabled,
                )}
                releaseStepType={releaseStepType}
            />
        </>
    );
};

type ReleaseEventsTimelineSectionProps = Omit<EventsTimelineSectionProps, 'events' | 'isLoading'>;

export const ReleaseEventsTimelineSection: React.FC<ReleaseEventsTimelineSectionProps> = (props) => {
    const { appId, releaseId, orgId, stepRunId, releaseStepType } = props;

    const workflowRunId = useSelector((state: RootState) => {
        const release = selectRelease(state, releaseId);
        return release.workflowRunId;
    });

    const workflowId = useSelector((state: RootState) => {
        const app = selectApp(state, appId);
        return app.workflowId;
    });

    const workflowRunEntityRequest: WorkflowRunEntityRequest = orgId &&
        workflowId &&
        workflowRunId && {
            orgId,
            workflowId,
            workflowRunId,
        };

    const timelineEventsEntityRequest: TimelineEventsEntityRequest = workflowRunEntityRequest &&
        stepRunId && {
            ...workflowRunEntityRequest,
            stepRunIds: [stepRunId],
        };

    const dispatch = useDispatch();

    const shouldLoadOnlyStepRunEvents = !!timelineEventsEntityRequest;

    const isLoadingReleaseTimelineEvents = useIsLoading(FetchableEntityType.ReleaseTimelineEvents, releaseId);
    const fetchReleaseTimelineEventsError = useError(FetchableEntityType.ReleaseTimelineEvents, releaseId);

    const [fetchTimelineEvents, isLoadingWorkflowRunTimelineEvents, fetchWorkflowRunTimelineEventsError] =
        useGetEntityV2(FetchableEntityTypeV2.TimelineEvents, stepRunId);

    const isLoadingTimelineEvents =
        workflowRunEntityRequest ? isLoadingWorkflowRunTimelineEvents : isLoadingReleaseTimelineEvents;
    const fetchTimelineEventsError =
        workflowRunEntityRequest ? fetchWorkflowRunTimelineEventsError : fetchReleaseTimelineEventsError;

    const release = useSelector((state: RootState) => selectRelease(state, props.releaseId)) as Release;

    const events = useSelector((state: RootState) => selectFilteredTimelineEvents(state, releaseId)) ?? [];

    const isDemoInstance = orgId ? useSelector((state: RootState) => selectIsDemoInstance(state, props.orgId)) : false;

    const workflowTimelineEvents = useSelector((state: RootState) => {
        return selectTimelineEvents(state, workflowRunId, stepRunId ? [stepRunId] : undefined);
    });

    const workflowEvents = release ? workflowTimelineEvents : [];

    React.useEffect(() => {
        if (shouldLoadOnlyStepRunEvents) {
            // do not fetch timeline events if we already have them
            if (workflowEvents.length > 0) {
                return;
            }

            fetchTimelineEvents(timelineEventsEntityRequest);
        } else if (appId && releaseId && !isDemoInstance) {
            if (events.length > 0) {
                return;
            }

            dispatch(
                tryFetchEntity(
                    FetchableEntityType.ReleaseTimelineEvents,
                    {
                        orgId,
                        appId,
                        releaseId,
                        releaseStepType,
                    },
                    releaseId,
                ),
            );
        }
    }, [appId, releaseId, releaseStepType]);

    React.useEffect(() => {
        if (fetchTimelineEventsError) {
            globalMessage.error('An error occurred when fetching release timeline events.');
        }
    }, [fetchTimelineEventsError]);

    return (
        <EventsTimelineSection
            {...props}
            events={timelineEventsEntityRequest ? workflowEvents : events}
            isLoading={isLoadingTimelineEvents}
        />
    );
};
